import type { FunctionComponent, ReactNode } from 'react';
import { Children } from 'react';
import cx from 'classnames';
import ListRowDetailsSeparator from './ListRowDetailsSeparator';

type Props = {
  //  Alters the styling of internal elements for displaying on a dark background.
  isDark?: boolean;
  //  Alters the wrapping of the children, allowing them to wrap to a new line. Defaults to false
  shouldWrap?: boolean;
};

/**
 * ListRowDetails handles displaying groups of
 * "details", with a separator applied between them.
 *
 * You can put any children in here.
 */
const ListRowDetails: FunctionComponent<Props> = ({ children, isDark, shouldWrap = false }) => {
  //  React is silly and counts `null` and `undefined` in `Children.count`,
  //  but excludes them when you convert them to an array with `Children.toArray`.
  const childrenCount = Children.toArray(children).length;
  const classNames = cx('fossa-list__row__details', {
    'fossa-list__row__details--dark': isDark,
    'fossa-list__row__details--wrap': shouldWrap,
  });

  //  No need for separators here.
  if (childrenCount < 2) {
    return <div className={classNames}>{children}</div>;
  }

  //  Convert to an array so we can use flatMap.
  const detailItemsAndSeparators = Children.toArray(children).flatMap((child: ReactNode, index: number) => {
    if (index === childrenCount - 1) {
      return child;
    }
    return [child, <ListRowDetailsSeparator />];
  });

  return <div className={classNames}>{detailItemsAndSeparators}</div>;
};

export default ListRowDetails;
